import { useEffect } from 'react';

/**
 * Hook to load Microsoft Clarity analytics
 * Automatically loads Clarity tracking script when component mounts
 *
 * Usage: Add `useClarity()` in App.jsx or main layout component
 *
 * Features tracked by Clarity:
 * - Session recordings (user interactions)
 * - Heatmaps (click/scroll/engagement)
 * - User behavior analytics
 * - Dead clicks and rage clicks detection
 *
 * @see https://clarity.microsoft.com/
 */
export function useClarity() {
  useEffect(() => {
    const clarityId = import.meta.env.VITE_CLARITY_PROJECT_ID;

    // Skip in development or if no Project ID configured
    if (!clarityId || clarityId === 'your-clarity-project-id-here') {
      console.log('[Clarity] Skipped: No Project ID configured');
      return;
    }

    // Skip if user has not consented to analytics cookies
    if (localStorage.getItem('cookie_consent') !== 'accepted') {
      console.log('[Clarity] Skipped: No cookie consent');
      return;
    }

    // Skip if already loaded
    if (window.clarity) {
      console.log('[Clarity] Already loaded');
      return;
    }

    // Load Clarity script
    console.log('[Clarity] Initializing...');

    (function(c, l, a, r, i, t, y) {
      c[a] = c[a] || function() {
        (c[a].q = c[a].q || []).push(arguments);
      };
      t = l.createElement(r);
      t.async = 1;
      t.src = "https://www.clarity.ms/tag/" + i;
      y = l.getElementsByTagName(r)[0];
      y.parentNode.insertBefore(t, y);
    })(window, document, "clarity", "script", clarityId);

    console.log('[Clarity] Loaded successfully');

    // Optional: Track custom events
    // window.clarity("event", "user_logged_in");

  }, []);
}

/**
 * Helper function to track custom Clarity events
 * Call this function anywhere in your app to log custom events
 *
 * @param {string} eventName - Name of the event (e.g., "trial_started", "property_created")
 * @param {object} metadata - Optional metadata to attach to the event
 *
 * @example
 * trackClarityEvent("trial_started", { plan: "pro" });
 * trackClarityEvent("property_created", { type: "apartment" });
 */
export function trackClarityEvent(eventName, metadata = {}) {
  if (window.clarity) {
    window.clarity("event", eventName);
    console.log(`[Clarity] Event tracked: ${eventName}`, metadata);
  } else {
    console.warn('[Clarity] Not loaded - event not tracked:', eventName);
  }
}
